import { useVirtualizer } from "@tanstack/react-virtual";
import { AnimatePresence } from "framer-motion";
import { SparklesIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import CreateContextMenu from "../CreateContextMenu";
import DroppableZone from "../DragAndDrop/DroppableZone";
import SelectionContainer from "../DragAndDrop/SelectionContainer";
import { useInitialEnterAnimation } from "../hooks/useInitialEnterAnimation";
import { RegistryItem } from "../types";
import { useUnifiedRegistry } from "../unified-registry-context";

import CardRouter from "./CardRouter";
import CreatePlaceholderCard from "./CreatePlaceholderCard";

import RequestVolumeTail, { REQUEST_VOLUME_TAIL_HEIGHT } from "@/components/RequestVolumeTail";
import { EntityType } from "@/queries/folder-entities/types";

const GridRowContent: React.FC<{
  row: GridRow;
  registry: { isFlattened: boolean };
  onItemClick?: (item: RegistryItem) => void;
  columnsCount?: number;
  virtualItemIndex?: number;
  currentFolderId: number | null;
  workspaceId?: number | null;

  shouldAnimateIn?: boolean;
}> = ({
  row,
  registry,
  onItemClick,
  columnsCount,
  virtualItemIndex,
  currentFolderId,
  workspaceId,

  shouldAnimateIn = false,
}) => {
  if (row.type === "banner") {
    return <RequestVolumeTail />;
  }

  if (row.type === "header") {
    return (
      <div
        className={`sticky top-0 z-10 bg-white px-8 pb-2 ${registry.isFlattened ? "pt-3" : "pt-6"}`}
      >
        <h2 className="flex items-center gap-1 text-sm font-normal text-gray-600">
          {row.semantic && <SparklesIcon className="h-3.5 w-3.5 text-amber-500" />}
          {row.label} <span className="text-gray-400">({row.totalCount})</span>
        </h2>
      </div>
    );
  }

  if (row.type === "empty") {
    return <div className="h-12" />;
  }

  const itemCount = row.items?.length || 0;
  const hasPlaceholder = !!row.createPlaceholder;
  const effectiveCount = itemCount + (hasPlaceholder ? 1 : 0);
  const emptySlots = columnsCount ? Math.max(0, columnsCount - effectiveCount) : 0;

  const rowHasNonFolderItems = row.items?.some(item => item.type !== EntityType.FOLDER);
  const fixedItemRowHeight = registry.isFlattened ? 170 : 160;

  return (
    <div
      className="overflow-hidden px-8"
      style={rowHasNonFolderItems ? { height: fixedItemRowHeight } : undefined}
    >
      <div
        className={`grid h-full grid-cols-1 ${
          registry.isFlattened ? "gap-3" : "gap-4"
        } sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4`}
      >
        <AnimatePresence mode="popLayout">
          {row.items?.map(item => (
            <div
              key={`${item.type}-${item.id}`}
              className={
                item.type === EntityType.FOLDER
                  ? "h-auto"
                  : "flex h-[160px] min-h-0 flex-col overflow-hidden"
              }
            >
              <div
                className={
                  item.type === EntityType.FOLDER
                    ? "h-auto"
                    : "flex h-full min-h-0 flex-col overflow-hidden"
                }
              >
                <CardRouter
                  card={item}
                  onItemClick={onItemClick}
                  shouldAnimateIn={shouldAnimateIn}
                />
              </div>
            </div>
          ))}
        </AnimatePresence>
        {hasPlaceholder && (
          <div
            className={
              row.createPlaceholder === "folder"
                ? "h-auto"
                : "flex h-[160px] min-h-0 flex-col overflow-hidden"
            }
          >
            <CreatePlaceholderCard
              variant={row.createPlaceholder!}
              currentFolderId={currentFolderId}
              workspaceId={workspaceId}
            />
          </div>
        )}
        {emptySlots > 0 &&
          Array.from({
            length: emptySlots,
          }).map((_, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={`empty-${virtualItemIndex}-col-${idx}`} className="invisible" />
          ))}
      </div>
    </div>
  );
};

interface VirtualizedCardGridProps {
  folders: RegistryItem[];
  items: RegistryItem[];
  allItems?: RegistryItem[];
  textResultCount?: number;
  isLoading?: boolean;
  currentFolderId: number | null;
  workspaceId?: number | null;
  onItemClick?: (item: RegistryItem) => void;
  showBanner?: boolean;
}

interface GridRow {
  type: "header" | "items" | "banner" | "empty";
  label?: string;
  items?: RegistryItem[];
  startIndex: number;
  totalCount?: number;
  createPlaceholder?: "folder" | "items";
  semantic?: boolean;
}

const VirtualizedCardGrid = ({
  folders,
  items,
  allItems: allItemsProp,
  textResultCount = 0,
  isLoading = false,
  currentFolderId,
  workspaceId,
  onItemClick,
  showBanner = true,
}: VirtualizedCardGridProps) => {
  const [columnsCount, setColumnsCount] = useState(4);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const shouldAnimateIn = useInitialEnterAnimation();
  const registry = useUnifiedRegistry();

  const calculateColumns = useCallback(() => {
    if (typeof window === "undefined") {
      return 4;
    }
    const width = window.innerWidth;
    if (width < 640) {
      return 1;
    } // sm breakpoint
    if (width < 1024) {
      return 2;
    } // lg breakpoint
    if (width < 1280) {
      return 3;
    } // xl breakpoint
    return 4;
  }, []);

  useEffect(() => {
    const updateColumns = () => {
      setColumnsCount(calculateColumns());
    };

    updateColumns();

    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateColumns, 150);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, [calculateColumns]);

  const rows = useMemo(() => {
    const result: GridRow[] = [];
    let startIndex = 0;

    if (showBanner) {
      result.push({
        type: "banner",
        startIndex,
      });
    }
    startIndex += 1;

    if (registry.debouncedSearchQuery) {
      const searchItems = allItemsProp || [...folders, ...items];
      const textItems = searchItems.slice(0, textResultCount);
      const semanticItems = searchItems.slice(textResultCount);

      for (let i = 0; i < textItems.length; i += columnsCount) {
        result.push({
          type: "items",
          items: textItems.slice(i, i + columnsCount),
          startIndex,
        });
        startIndex += 1;
      }

      if (semanticItems.length > 0) {
        const hasTextResults = textItems.length > 0;
        result.push({
          type: "header",
          label: hasTextResults ? "Other relevant results" : "Relevant results",
          startIndex,
          totalCount: semanticItems.length,
          semantic: true,
        });
        startIndex += 1;

        for (let i = 0; i < semanticItems.length; i += columnsCount) {
          result.push({
            type: "items",
            items: semanticItems.slice(i, i + columnsCount),
            startIndex,
          });
          startIndex += 1;
        }
      }
    } else if (registry.isFlattened) {
      const allItems = [...folders, ...items];
      const groupedByPath = allItems.reduce(
        (acc, item) => {
          const pathKey =
            item.type === EntityType.FOLDER
              ? item.path.slice(0, item.path.length - 1).join("/")
              : item.path.join("/");

          if (!acc[pathKey]) {
            acc[pathKey] = [];
          }
          acc[pathKey].push(item);
          return acc;
        },
        {} as Record<string, RegistryItem[]>,
      );

      const sortedPaths = Object.keys(groupedByPath).sort();

      sortedPaths.forEach(pathKey => {
        const pathItems = groupedByPath[pathKey];
        const displayPath = pathKey || "Home";

        result.push({
          type: "header",
          label: displayPath,
          startIndex,
          totalCount: pathItems.length,
        });
        startIndex += 1;

        for (let i = 0; i < pathItems.length; i += columnsCount) {
          const isLastChunk = i + columnsCount >= pathItems.length;
          const chunkItems = pathItems.slice(i, i + columnsCount);
          const hasRoom = chunkItems.length < columnsCount;

          result.push({
            type: "items",
            items: chunkItems,
            startIndex,
            createPlaceholder: isLastChunk && hasRoom ? "items" : undefined,
          });
          startIndex += 1;
        }

        // If last chunk was full, add a dedicated placeholder row
        if (pathItems.length > 0 && pathItems.length % columnsCount === 0) {
          result.push({
            type: "items",
            items: [],
            startIndex,
            createPlaceholder: "items",
          });
          startIndex += 1;
        }
      });
    } else {
      if (folders.length > 0) {
        result.push({
          type: "header",
          label: "Folders",
          startIndex,
          totalCount: folders.length,
        });
        startIndex += 1;

        for (let i = 0; i < folders.length; i += columnsCount) {
          const isLastChunk = i + columnsCount >= folders.length;
          const chunkItems = folders.slice(i, i + columnsCount);
          const hasRoom = chunkItems.length < columnsCount;

          result.push({
            type: "items",
            items: chunkItems,
            startIndex,
            createPlaceholder: isLastChunk && hasRoom ? "folder" : undefined,
          });
          startIndex += 1;
        }

        // If last folder row was full, add a dedicated placeholder row
        if (folders.length % columnsCount === 0) {
          result.push({
            type: "items",
            items: [],
            startIndex,
            createPlaceholder: "folder",
          });
          startIndex += 1;
        }
      }

      if (items.length > 0) {
        result.push({
          type: "header",
          label: "Items",
          startIndex,
          totalCount: items.length,
        });
        startIndex += 1;

        for (let i = 0; i < items.length; i += columnsCount) {
          const isLastChunk = i + columnsCount >= items.length;
          const chunkItems = items.slice(i, i + columnsCount);
          const hasRoom = chunkItems.length < columnsCount;

          result.push({
            type: "items",
            items: chunkItems,
            startIndex,
            createPlaceholder: isLastChunk && hasRoom ? "items" : undefined,
          });
          startIndex += 1;
        }

        // If last items row was full, add a dedicated placeholder row
        if (items.length % columnsCount === 0) {
          result.push({
            type: "items",
            items: [],
            startIndex,
            createPlaceholder: "items",
          });
          startIndex += 1;
        }
      }
    }

    result.push({
      type: "empty",
      startIndex,
    });

    return result;
  }, [
    folders,
    items,
    allItemsProp,
    textResultCount,
    columnsCount,
    registry.isFlattened,
    registry.debouncedSearchQuery,
    showBanner,
  ]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: useCallback(
      (index: number) => {
        const row = rows[index];

        if (row?.type === "banner") {
          return REQUEST_VOLUME_TAIL_HEIGHT;
        }
        if (row?.type === "header") {
          return registry.isFlattened ? 48 : 56;
        }
        if (row?.type === "empty") {
          return 48;
        }

        // Placeholder-only rows (no real items)
        if (row?.items?.length === 0 && row?.createPlaceholder) {
          return row.createPlaceholder === "folder" ? 64 : 160 + 16;
        }

        if (registry.isFlattened) {
          // In flattened mode, use consistent height based on actual content
          const hasNonFolderItems = row?.items?.some(item => item.type !== EntityType.FOLDER);
          return hasNonFolderItems ? 170 : 76; // 170px for items with body, 76px for folders only
        }

        const hasFolder = row?.items?.some(item => item.type === EntityType.FOLDER);
        const hasItems = row?.items?.some(item => item.type !== EntityType.FOLDER);

        if (hasFolder && !hasItems) {
          return 64; // Compact height for folder rows
        } else if (!hasFolder && hasItems) {
          return 160 + 16; // Add 16px for gap-4 (1rem) for items
        }
        return 220 + 16; // Add 16px for gap-4 (1rem) for mixed
      },
      [rows, registry.isFlattened],
    ),
    overscan: 3,
    getItemKey: useCallback(
      (index: number) => {
        const row = rows[index];

        if (row?.type === "banner") {
          return `banner-top`;
        }
        if (row?.type === "header") {
          return `header-${row.label}`;
        }
        if (row?.items && row.items.length > 0) {
          const suffix = row.createPlaceholder ? `-placeholder-${row.createPlaceholder}` : "";
          return `row-${index}-${row.items.map(item => `${item.type}-${item.id}`).join("-")}${suffix}`;
        }
        if (row?.createPlaceholder) {
          return `placeholder-row-${row.createPlaceholder}-${index}`;
        }
        return `row-${index}`;
      },
      [rows],
    ),
  });

  const virtualItems = virtualizer.getVirtualItems();

  if (isLoading || rows.length === 0) {
    return null;
  }

  return (
    <CreateContextMenu currentFolderId={currentFolderId}>
      <SelectionContainer
        items={allItemsProp || [...folders, ...items]}
        className="relative h-full"
      >
        <div className="relative h-full">
          <DroppableZone id="root-drop-zone" isRootZone />

          <div
            ref={scrollContainerRef}
            className="h-full overflow-auto"
            data-scroll-container
            style={{
              contain: "strict",
            }}
          >
            <div
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: "100%",
                position: "relative",
              }}
            >
              <AnimatePresence mode="popLayout">
                {virtualItems.map(virtualItem => {
                  const row = rows[virtualItem.index];
                  if (!row) {
                    return null;
                  }

                  return (
                    <div
                      key={virtualItem.key}
                      data-index={virtualItem.index}
                      ref={
                        row.type === "items" &&
                        row.items?.some(item => item.type !== EntityType.FOLDER)
                          ? undefined
                          : virtualizer.measureElement
                      }
                      style={{
                        height:
                          row.type === "items" &&
                          row.items?.some(item => item.type !== EntityType.FOLDER)
                            ? virtualItem.size
                            : undefined,
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                    >
                      <GridRowContent
                        row={row}
                        registry={registry}
                        onItemClick={onItemClick}
                        columnsCount={columnsCount}
                        virtualItemIndex={virtualItem.index}
                        currentFolderId={currentFolderId}
                        workspaceId={workspaceId}
                        shouldAnimateIn={shouldAnimateIn}
                      />
                    </div>
                  );
                })}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </SelectionContainer>
    </CreateContextMenu>
  );
};

export default observer(VirtualizedCardGrid);
